import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * App-wide error boundary. Catches render-time crashes and shows a recoverable
 * fallback (reload / sign out). React Query errors are NOT caught here — those
 * surface as query.error and should be handled by the relevant feature view.
 */
interface State {
  error: Error | null;
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // biome-ignore lint/suspicious/noConsole: top-level crash logger
    console.error("[AppErrorBoundary] crash", error, info.componentStack);
  }

  private reset = () => {
    this.setState({ error: null });
  };

  private hardReset = () => {
    try {
      localStorage.removeItem("coach.license.token");
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex min-h-svh items-center justify-center bg-background p-6">
        <div className="w-full max-w-md rounded-xl border border-white/10 bg-white/4 p-6 backdrop-blur">
          <h1 className="text-title-md font-semibold">Etwas ist schiefgelaufen</h1>
          <p className="mt-2 text-body-sm text-muted-foreground">
            Die App ist in einen Fehler gelaufen. Deine Daten sind sicher in der lokalen Datenbank —
            du kannst es einfach noch einmal versuchen.
          </p>
          <pre className="mt-4 max-h-40 overflow-auto rounded-md bg-black/40 p-3 text-label-caps text-red-300">
            {this.state.error.message}
          </pre>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="flex-1 rounded-md bg-pitch-lime px-3 py-2 text-body-md font-semibold text-[#1d3700]"
            >
              Erneut versuchen
            </button>
            <button
              type="button"
              onClick={this.hardReset}
              className="rounded-md border border-white/15 px-3 py-2 text-body-md text-muted-foreground"
            >
              Neu starten
            </button>
          </div>
        </div>
      </div>
    );
  }
}
